import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { getApiClient, ApiProblemError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { AUTH_BRAND, A_SESSION_COPY } from '@/copy/registry'

/**
 * `a-session` — the refresh-failure state, and "the ONLY way that screen is reached" (§3.1i rule
 * 13). No countdown, no idle timer — the inactivity model is the refresh token itself.
 * `identityRefreshSession` already failed to bring us here; the one control calls
 * `identitySignOut` to clear any remaining cookie state before returning to sign-in.
 */
export function ASession({ onSignedOut }: { onSignedOut: () => void }) {
  const signOut = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { error } = await client.POST('/auth/sign-out', { body: { global: false } })
      if (error) throw new ApiProblemError(error as Problem)
    },
    onSettled: () => {
      setAccessToken(null)
      onSignedOut()
    },
  })

  return (
    <AuthCard unitId={unitId('a-session')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_SESSION_COPY.heading}</h3>
      <AuthCardLead>{A_SESSION_COPY.lead}</AuthCardLead>

      <Button data-act={actionId('session.signOut')} disabled={signOut.isPending} onClick={() => signOut.mutate()}>
        {signOut.isPending ? 'Signing in…' : A_SESSION_COPY.submitButton}
      </Button>

      <Note>{A_SESSION_COPY.noTimerNote}</Note>
    </AuthCard>
  )
}
